import { FileText } from 'lucide-react';
import { useState } from 'react';

import {
  type NoteIconGroupName,
  type NoteIconName,
  type NoteIconTone,
  noteIconGroups,
  noteIconTones,
} from '../../shared/note-icon';

import { Button, ControlPopover, IconButton, NoteIcon, SearchField, Select } from './ui';

const label = (name: string) =>
  name
    .split('-')
    .map((part, index) => (index === 0 ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');

const toneLabels: Record<NoteIconTone, string> = {
  neutral: 'Neutral',
  priority: 'Yellow',
  info: 'Blue',
  focus: 'Violet',
  success: 'Green',
  attention: 'Peach',
};

/**
 * A drawer name and the words someone would search for its contents. Every icon in a
 * group inherits its terms, so "money" reaches the whole shopping drawer even though
 * no glyph is called that.
 */
const groupLabels: Record<NoteIconGroupName, { label: string; terms: string }> = {
  documents: { label: 'Documents & notes', terms: 'document note page paper writing reading' },
  work: { label: 'Work & planning', terms: 'work project plan task goal time schedule meeting' },
  people: { label: 'People & messages', terms: 'people person contact message mail chat social' },
  tech: { label: 'Devices & tech', terms: 'device hardware computer network gadget' },
  code: { label: 'Code & data', terms: 'code programming developer data database git' },
  media: { label: 'Media & creative', terms: 'media music photo video art design creative' },
  tools: { label: 'Tools & security', terms: 'tool object key lock security repair clothing' },
  money: { label: 'Money & shopping', terms: 'money finance payment shopping order budget' },
  places: { label: 'Home & buildings', terms: 'home house building room furniture place' },
  travel: { label: 'Travel & transport', terms: 'travel trip transport vehicle map location' },
  nature: { label: 'Nature & weather', terms: 'nature weather plant garden outdoor season sky' },
  animals: { label: 'Animals', terms: 'animal pet wildlife' },
  food: { label: 'Food & drink', terms: 'food drink meal cooking recipe' },
  health: { label: 'Health & body', terms: 'health body medical fitness wellbeing' },
  sports: { label: 'Games & sport', terms: 'game sport play hobby award' },
  science: {
    label: 'Science & learning',
    terms: 'science study school research maths measurement',
  },
  symbols: { label: 'Shapes & symbols', terms: 'shape symbol sign mark star zodiac' },
};

/** Words for icons whose drawn name is not what someone would type to find them. */
const iconTerms: Partial<Record<NoteIconName, string>> = {
  activity: 'pulse heartbeat metrics',
  'alarm-clock': 'reminder wake',
  archive: 'storage keep',
  'at-sign': 'email mention handle',
  banknote: 'cash bill salary',
  'bell-off': 'muted silent',
  'book-open': 'reading study',
  'bot-message-square': 'assistant chatbot ai',
  brain: 'thinking idea learning',
  briefcase: 'job business career',
  'calendar-days': 'month agenda',
  'chart-line': 'growth metrics analytics',
  'circle-alert': 'warning error problem risk',
  'circle-check': 'done complete resolved',
  'circle-user': 'profile account avatar',
  'clipboard-list': 'checklist agenda minutes',
  clock: 'time schedule duration',
  cog: 'settings preferences configuration',
  compass: 'direction explore navigate',
  'credit-card': 'payment subscription billing',
  crosshair: 'precision aim',
  'drafting-compass': 'architecture blueprint',
  droplet: 'water hydration',
  dumbbell: 'gym exercise workout',
  'file-spreadsheet': 'excel csv table numbers',
  'file-text': 'document page write',
  flag: 'milestone marker priority',
  flame: 'streak hot urgent',
  'flask-conical': 'experiment lab chemistry',
  'folder-open': 'directory files',
  gauge: 'speed performance dashboard',
  gavel: 'legal court decision judgement',
  gem: 'valuable premium jewel',
  'git-branch': 'version control repository',
  'graduation-cap': 'education university course degree',
  'hand-coins': 'salary payout income',
  handshake: 'agreement deal partnership',
  heart: 'love favourite personal',
  'heart-pulse': 'health vitals cardio',
  history: 'past log timeline revisions',
  hourglass: 'waiting deadline countdown',
  house: 'home household',
  'id-card': 'identity profile badge',
  inbox: 'incoming triage',
  landmark: 'bank government institution',
  laptop: 'computer work machine',
  leaf: 'green sustainability plant',
  library: 'reference collection shelf',
  lightbulb: 'idea insight inspiration',
  'list-todo': 'todo tasks checklist backlog',
  lock: 'private secure password',
  'map-pin': 'location place address',
  megaphone: 'announcement marketing launch',
  'message-square': 'chat conversation feedback',
  milestone: 'checkpoint release stage',
  'monitor-play': 'presentation demo screen',
  moon: 'night sleep evening dark',
  'notebook-pen': 'journal diary writing',
  package: 'delivery shipping release',
  palette: 'colours design theme',
  paperclip: 'attachment file',
  'pen-tool': 'design vector drawing',
  'piggy-bank': 'savings budget',
  'pill-bottle': 'medication prescription',
  plane: 'flight holiday trip',
  presentation: 'slides talk deck',
  receipt: 'expense invoice purchase',
  'refresh-cw': 'sync update repeat',
  rocket: 'launch startup ship',
  route: 'journey plan path steps',
  scale: 'balance law justice compare',
  scroll: 'history archive charter',
  search: 'find lookup research',
  send: 'submit share deliver',
  server: 'backend hosting infrastructure',
  'shield-check': 'security trusted protected',
  'shopping-cart': 'purchase order basket',
  sparkles: 'new highlight magic',
  sprout: 'growth beginning seedling',
  'square-kanban': 'board backlog workflow',
  'square-terminal': 'shell command console',
  star: 'favourite rating important',
  'sticky-note': 'reminder scratch memo',
  stethoscope: 'doctor appointment clinic',
  sun: 'day morning bright light',
  target: 'goal objective aim okr',
  telescope: 'vision strategy outlook',
  timer: 'focus pomodoro countdown',
  'trending-up': 'growth improvement progress',
  'user-round': 'person profile someone',
  users: 'team group meeting people',
  utensils: 'restaurant dining meal',
  vault: 'secure credentials safe',
  wallet: 'money account spending',
  wand: 'magic automation generate',
  workflow: 'process pipeline automation',
  zap: 'quick energy action',
};

type Entry = { icon: NoteIconName; group: NoteIconGroupName; text: string };

/**
 * The catalogue is big enough that the picker shows one group at a time; search is
 * the way across all of it, so each icon carries its own words plus its group's.
 * Built once, on the first search rather than at import.
 */
let index: Entry[] | null = null;

const searchable = () =>
  (index ??= noteIconGroups.flatMap(group =>
    group.icons.map(icon => ({
      icon,
      group: group.name,
      text: `${icon} ${label(icon)} ${groupLabels[group.name].label} ${groupLabels[group.name].terms} ${iconTerms[icon] ?? ''}`.toLocaleLowerCase(),
    })),
  ));

const groupOf = (icon: string) =>
  noteIconGroups.find(group => (group.icons as readonly string[]).includes(icon))?.name;

/** Enough to browse without the panel rendering the whole catalogue on a broad search. */
const shownAtOnce = 140;

export function NoteIconPicker({
  disabled,
  icon,
  tone,
  onChange,
}: {
  disabled?: boolean;
  icon: string | null;
  tone: string | null;
  onChange: (icon: string | null, tone: string | null) => void;
}) {
  const [open, setOpen] = useState(false),
    [search, setSearch] = useState(''),
    [group, setGroup] = useState<NoteIconGroupName>('documents');

  const choose = (nextIcon: string | null, nextTone: string | null = tone) => {
    onChange(nextIcon, nextIcon ? nextTone : null);
    setOpen(false);
  };

  const terms = search.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);

  const matches = terms.length
    ? searchable().filter(entry => terms.every(term => entry.text.includes(term)))
    : searchable().filter(entry => entry.group === group);

  const shown = matches.slice(0, shownAtOnce);

  return (
    <ControlPopover
      label="Page icon"
      open={open}
      onOpenChange={next => {
        setOpen(next);

        if (next) {
          setSearch('');
          setGroup((icon && groupOf(icon)) || 'documents');
        }
      }}
      trigger={
        <IconButton
          disabled={disabled}
          className="note-icon-picker"
          label={icon ? 'Change page icon' : 'Add page icon'}
        >
          {icon ? <NoteIcon icon={icon} tone={tone} /> : <FileText aria-hidden="true" />}
        </IconButton>
      }
    >
      <strong>Page icon</strong>
      <SearchField label="Search icons" value={search} onChange={setSearch} />
      {terms.length ? (
        <p className="muted note-icon-hint">
          {matches.length === 1 ? '1 icon matches' : `${matches.length} icons match`}
          {matches.length > shownAtOnce ? `, showing the first ${shownAtOnce}` : ''}
        </p>
      ) : (
        <div className="note-icon-group">
          <Select
            aria-label="Icon group"
            value={group}
            onChange={event => setGroup(event.target.value as NoteIconGroupName)}
          >
            {noteIconGroups.map(entry => (
              <option key={entry.name} value={entry.name}>
                {groupLabels[entry.name].label}
              </option>
            ))}
          </Select>
        </div>
      )}
      <div className="note-icon-grid">
        {shown.map(entry => (
          <IconButton
            key={entry.icon}
            label={`Use ${label(entry.icon)} icon`}
            aria-pressed={icon === entry.icon}
            className={icon === entry.icon ? 'active' : ''}
            onClick={() => choose(entry.icon)}
          >
            <NoteIcon icon={entry.icon} />
          </IconButton>
        ))}
        {!matches.length && <p className="muted">No icon matches that search.</p>}
      </div>
      <div className="note-icon-tones">
        <strong>Background</strong>
        <div className="note-icon-tone-row">
          {noteIconTones.map(name => (
            <IconButton
              key={name}
              label={`${toneLabels[name]} background`}
              aria-pressed={(tone ?? 'neutral') === name}
              className={`note-icon-tone tone-${name} ${(tone ?? 'neutral') === name ? 'active' : ''}`}
              disabled={!icon}
              onClick={() => choose(icon, name)}
            >
              <span className="note-icon-swatch" aria-hidden="true" />
            </IconButton>
          ))}
        </div>
        {!icon && <p className="muted">Choose an icon first.</p>}
      </div>
      {icon && (
        <div className="button-row">
          <Button variant="ghost" onClick={() => choose(null, null)}>
            Remove icon
          </Button>
        </div>
      )}
    </ControlPopover>
  );
}
